import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, getFirestore } from 'firebase/firestore';
// import { db } from '../../firebase';
import {defaultRestaurantData} from './res-data';
import JSONEditor from 'jsoneditor';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import 'jsoneditor/dist/jsoneditor.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './Style.scss';
interface RestaurantData {
  id?: string;
  name: string;
  foodType: string;
  address: string;
  phone: string;
  gstRate: number;
  serviceRate: number;
  tabs: {
    table: number;
    [key: string]: number;
  };
  menu: Record<string, any>;
  categoryOrder: string[];
  isProd: boolean;
  discountPercent: number;
  extraDiscountPercent: number;
  createdAt?: string;
}

interface _ValidationError {
  path: string[];
  message: string;
}

const RestaurantsTab = () => {
  const [restaurants, setRestaurants] = useState<RestaurantData[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantData | null>(null);
  const [showEditor, setShowEditor] = useState<boolean>(false);
  const editorRef = useRef<JSONEditor | null>(null);
  const jsonEditorRef = useRef<HTMLDivElement>(null);

  const columns: any = [
    { field: 'name', headerName: 'Restaurant Name', sortable: true, filter: true, flex: 1, minWidth: 100 },
    { field: 'foodType', headerName: 'Type', sortable: true, filter: true, flex: 1, minWidth: 100 },
    { field: 'address', headerName: 'Address', sortable: true, filter: true, flex: 1.5, minWidth: 100 },
    { field: 'phone', headerName: 'Phone', sortable: true, filter: true, flex: 1, minWidth: 100 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filter: false,
      minWidth: 50,
      cellRenderer: (params: { data: RestaurantData }) => (
        <Tooltip title="Edit Restaurant">
          <IconButton
            size="small"
            onClick={() => handleEdit(params.data)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async (): Promise<void> => {
    try {
      const db = getFirestore();
      const querySnapshot = await getDocs(collection(db, 'restaurants'));
      const restaurantsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as RestaurantData[];
      setRestaurants(restaurantsList);
    } catch (error) {
      console.error('Error loading restaurants:', error);
      alert('Failed to load restaurants');
    }
  };

  const validateRestaurantData = (data: RestaurantData): string[] => {
    const requiredFields = ['name', 'address', 'phone'];
    const errors: string[] = [];

    // Check required fields
    requiredFields.forEach(field => {
      if (!data[field as keyof RestaurantData]) {
        errors.push(`${field} is required`);
      }
    });

    // Validate phone number format
    if (data.phone && !/^\+?\d{10,12}$/.test(data.phone)) {
      errors.push('Invalid phone number format');
    }

    // Validate numeric fields
    if (typeof data.gstRate !== 'number' || data.gstRate < 0) {
      errors.push('GST rate must be a non-negative number');
    }
    if (typeof data.serviceRate !== 'number' || data.serviceRate < 0) {
      errors.push('Service rate must be a non-negative number');
    }

    return errors;
  };

  const handleEdit = (restaurant: Partial<RestaurantData>): void => {
    const dataToEdit = restaurant.id 
      ? { ...restaurant }
      : { ...defaultRestaurantData };
    
    setSelectedRestaurant(dataToEdit as RestaurantData);
    setShowEditor(true);
  };

  // Initialize editor when modal is opened
  useEffect(() => {
    const initializeEditor = () => {
      if (showEditor && selectedRestaurant && jsonEditorRef.current) {
        const container = jsonEditorRef.current;

        const options: any = {
          mode: 'code',
          onValidate: (json: RestaurantData) => {
            const errors = validateRestaurantData(json);
            return errors.map(error => ({
              path: [],
              message: error
            }));
          }
        };

        // Destroy existing editor if it exists
        if (editorRef.current) {
          editorRef.current.destroy();
        }

        // Create new editor
        editorRef.current = new JSONEditor(container, options);
        editorRef.current.set(selectedRestaurant);
      }
    };

    // Add a small delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeEditor, 100);
    return () => clearTimeout(timeoutId);
  }, [showEditor, selectedRestaurant]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, []);

  const handleCloseEditor = () => {
    if (editorRef.current) {
      editorRef.current.destroy();
      editorRef.current = null;
    }
    setShowEditor(false);
    setSelectedRestaurant(null);
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (!editorRef.current) return;

      const updatedData: any = editorRef.current.get() as RestaurantData;
      
      // Validate the data
      const validationErrors = validateRestaurantData(updatedData);
      if (validationErrors.length > 0) {
        alert(`Validation errors:\n${validationErrors.join('\n')}`);
        return;
      }

      const db = getFirestore();
      if (selectedRestaurant?.id) {
        // Update existing restaurant
        await updateDoc(doc(db, 'restaurants', selectedRestaurant.id), updatedData);
      } else {
        // Add new restaurant
        await addDoc(collection(db, 'restaurants'), {
          ...updatedData,
          createdAt: new Date().toISOString()
        });
      }
      
      handleCloseEditor();
      loadRestaurants();
    } catch (error) {
      console.error('Error saving restaurant:', error);
      alert(`Error saving restaurant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  console.log(restaurants)
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Restaurants
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleEdit({})}
        >
          Add
        </Button>
      </Box>

      <Paper elevation={2} sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
          <AgGridReact
            rowData={restaurants}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            rowHeight={48}
            headerHeight={48}
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true
            }}
          />
        </div>
      </Paper>

      <Dialog
        open={showEditor}
        onClose={handleCloseEditor}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedRestaurant?.id ? 'Edit Restaurant' : 'Add New Restaurant'}
        </DialogTitle>
        <DialogContent dividers>
          <div 
            ref={jsonEditorRef}
            style={{ 
              height: '60vh',
              minHeight: '400px',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseEditor}
            startIcon={<CancelIcon />}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RestaurantsTab; 
